import { useEffect, useState } from "react";
import api, { formatApiError } from "@/utils/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { Download, CheckCircle2 } from "lucide-react";
import useDocumentTitle from "@/hooks/useDocumentTitle";
import { GovMarkdown, downloadFile, fmtDate } from "@/components/governance/govUtils";
import { useBranding } from "@/contexts/BrandingContext";

const AcceptForm = ({ doc, onDone }) => {
  const { company_name } = useBranding() || {};
  const [f, setF] = useState({ organisation: company_name || "", signatory_name: "", signatory_title: "", authority_confirmed: false });
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault(); setBusy(true);
    try { await api.post(`/governance/my-agreements/${doc.id}/accept`, { ...f, document_sha256: doc.content_sha256 }); toast.success("Acceptance recorded"); onDone(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Failed"); }
    finally { setBusy(false); }
  };
  return (
    <form onSubmit={submit} className="mt-3 grid md:grid-cols-3 gap-2 rounded-md border border-sky-200 bg-sky-50/50 p-3" data-testid={`agreement-accept-form-${doc.doc_type}`}>
      {[["organisation", "Organisation (legal name)"], ["signatory_name", "Your full name"], ["signatory_title", "Your job title"]].map(([k, l]) => (
        <div key={k}><Label className="text-xs">{l}</Label><Input required value={f[k]} onChange={(e) => setF({ ...f, [k]: e.target.value })} data-testid={`agreement-${k}`} /></div>
      ))}
      <label className="md:col-span-3 flex items-start gap-2 text-xs"><Checkbox checked={f.authority_confirmed} onCheckedChange={(v) => setF({ ...f, authority_confirmed: !!v })} data-testid="agreement-authority" />
        I have read this document (version {doc.version}) and I am authorised to accept it on behalf of the organisation named above.</label>
      <div className="md:col-span-3"><Button type="submit" disabled={busy || !f.authority_confirmed} data-testid="agreement-accept-btn">Accept on behalf of organisation</Button></div>
    </form>
  );
};

export default function AgreementsPage() {
  useDocumentTitle("Agreements");
  const [docs, setDocs] = useState(null);
  const [open, setOpen] = useState(null);
  const load = () => api.get("/governance/my-agreements").then(({ data }) => setDocs(data)).catch(() => setDocs([]));
  useEffect(() => { load(); }, []);
  if (!docs) return null;
  return (
    <div className="space-y-4 max-w-4xl" data-testid="agreements-page">
      <div>
        <h1 className="text-2xl font-heading font-bold tracking-tight">Agreements & policies</h1>
        <p className="text-sm text-muted-foreground">Documents from your software provider, Castlewood Software. Only finalised versions appear here.</p>
      </div>
      {docs.length === 0 && <div className="bg-white border border-border rounded-md p-6 text-sm text-muted-foreground" data-testid="agreements-empty">No agreements have been issued yet.</div>}
      {docs.map((d) => (
        <div key={d.id} className="bg-white border border-border rounded-md p-4" data-testid={`agreement-${d.doc_type}`}>
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div>
              <p className="font-medium">{d.title} <span className="text-xs text-muted-foreground">v{d.version} · effective {fmtDate(d.effective_date)}</span></p>
              {d.acceptance ? <p className="text-xs text-emerald-700 flex items-center gap-1 mt-0.5"><CheckCircle2 size={12} /> Accepted by {d.acceptance.signatory_name} on {fmtDate(d.acceptance.created_at)}</p>
                : d.customer_acceptance_required ? <Badge variant="outline" className="mt-1 text-[10px] bg-amber-50 text-amber-800 border-amber-200">Awaiting your acceptance</Badge> : null}
            </div>
            <div className="flex gap-2">
              <Button size="sm" variant="outline" onClick={() => setOpen(open === d.id ? null : d.id)} data-testid={`agreement-view-${d.doc_type}`}>{open === d.id ? "Hide" : "Read"}</Button>
              <Button size="sm" variant="outline" className="gap-1" onClick={() => downloadFile(`/governance/documents/${d.id}/pdf`, `${d.doc_type}.pdf`)} data-testid={`agreement-pdf-${d.doc_type}`}><Download size={13} /> PDF</Button>
            </div>
          </div>
          {open === d.id && <div className="mt-3 max-h-[50vh] overflow-y-auto rounded-md border border-border p-4"><GovMarkdown>{d.content}</GovMarkdown></div>}
          {open === d.id && d.customer_acceptance_required && !d.acceptance && <AcceptForm doc={d} onDone={() => { setOpen(null); load(); }} />}
        </div>
      ))}
    </div>
  );
}
